import { uiText, useUiLanguage } from '../../utils/uiTranslation';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';

const EMPTY = { email: 'idle', phone: 'idle' };
const validValue = value => value && !['nan', 'No Email On File', 'No Phone Number'].includes(value);

function readProgress(key) {
  try {
    const saved = JSON.parse(sessionStorage.getItem(key));
    return saved?.expiresAt > Date.now() ? saved : null;
  } catch { return null; }
}

function saveProgress(key, states, pending = false) {
  const incomplete = pending || Object.values(states).some(status => ['pending', 'loading', 'error'].includes(status));
  const ttl = incomplete || !Object.values(states).includes('available') ? 86400000 : 90 * 86400000;
  try {
    // Store progress only, never contact values. Reopens retrieve authorized cached contacts.
    sessionStorage.setItem(key, JSON.stringify({ states, pending, expiresAt: Date.now() + ttl }));
  } catch { /* Storage is optional; the mounted view can still finish the lookup. */ }
}

function waitForNextCheck(signal, milliseconds) {
  return new Promise(resolve => {
    const finish = () => {
      clearTimeout(timer);
      signal.removeEventListener('abort', finish);
      resolve();
    };
    const timer = setTimeout(finish, milliseconds);
    signal.addEventListener('abort', finish, { once: true });
    if (signal.aborted) finish();
  });
}

export default function TalentContactLookup({ external, profileId, accountUid, apiBase,
  email, phone, onUpdate, onCopyEmail, onCopyPhone, emailCopied, phoneCopied,
  t = (_, fallback) => fallback }) {
  useUiLanguage();
  const [states, setStates] = useState(EMPTY);
  const [values, setValues] = useState({ email: '', phone: '' });
  const [error, setError] = useState('');
  const active = useRef(null);
  const latest = useRef({ onUpdate, email, phone });
  latest.current = { onUpdate, email, phone };
  const key = `tse-contact-progress:${accountUid}:${profileId}`;

  const startLookup = useCallback(async (pollExisting = false) => {
    if (!external || !accountUid || !profileId || active.current) return;
    const controller = new AbortController();
    active.current = controller;
    setError('');
    setStates(current => ({ email: current.email === 'available' ? 'available' : 'pending',
      phone: current.phone === 'available' ? 'available' : 'pending' }));
    saveProgress(key, { email: 'pending', phone: 'pending' });
    let checks = 0;
    try {
      while (!controller.signal.aborted) {
        const { data } = await axios.post(`${apiBase}/v2/get_contacts`,
          { profile_id: profileId, poll: pollExisting === true || checks > 0 },
          { params: { user_uid: accountUid }, signal: controller.signal });
        if (controller.signal.aborted) return;
        if (!['success', 'pending', 'not_found'].includes(data.status)) {
          // Never render backend/provider text: it can contain vendor names,
          // account balances, or infrastructure details unrelated to employers.
          throw new Error('contact_lookup_failed');
        }
        const pending = data.status === 'pending' || Boolean(data.pending);
        const next = Object.fromEntries(['email', 'phone'].map(field => [field,
          data[field] ? 'available' : data.contact_status?.[field] || (pending ? 'pending' : 'not_found')]));
        setValues(current => ({ email: data.email || current.email, phone: data.phone || current.phone }));
        setStates(next);
        saveProgress(key, next, pending);
        latest.current.onUpdate?.(data);
        const hasPending = Object.values(next).includes('pending');
        // A work email may already be visible while a personal email is sought.
        if (!hasPending && !pending) break;
        // Slow down long-running callbacks without starting new provider lookups.
        await waitForNextCheck(controller.signal, ++checks < 12 ? 3000 : 15000);
      }
    } catch (exception) {
      if (controller.signal.aborted) return;
      setError('contact_lookup_failed');
      setStates(current => {
        const next = Object.fromEntries(Object.entries(current).map(([field, status]) =>
          [field, ['available', 'not_found'].includes(status) ? status : 'error']));
        saveProgress(key, next);
        return next;
      });
    } finally {
      if (active.current === controller) active.current = null;
    }
  }, [external, accountUid, profileId, apiBase, key]);

  useEffect(() => {
    setValues({ email: '', phone: '' });
    setError('');
    const saved = external ? readProgress(key) : null;
    setStates(saved?.states || EMPTY);
    if (saved && (saved.pending || Object.values(saved.states).includes('pending') ||
      (saved.states.email === 'available' && !validValue(latest.current.email)) ||
      (saved.states.phone === 'available' && !validValue(latest.current.phone)))) {
      startLookup(true);
    }
    return () => {
      active.current?.abort();
      active.current = null;
    };
  }, [external, key, startLookup]);

  const field = (name, initialValue, icon, copy, copied) => {
    const value = validValue(values[name]) ? values[name] : validValue(initialValue) ? initialValue : '';
    const status = value ? 'available' : states[name];
    const pending = external && status === 'pending';
    const canLookup = external && ['idle', 'error'].includes(status);
    const label = value || (pending ? t('talentProfile.fetching', 'Fetching...') : canLookup
      ? (status === 'error' ? t('talentProfile.retryContact', 'Retry lookup') : name === 'email'
        ? t('talentProfile.getEmail', 'Get Email') : t('talentProfile.getPhone', 'Get Phone'))
      : name === 'email' ? t('talentProfile.noEmail', 'No Email') : t('talentProfile.noPhone', 'No Phone'));
    const click = value ? copy : canLookup ? () => startLookup() : undefined;
    return <div className={`tp-contact-item${canLookup ? ' tp-contact-unlock' : ''}`}
      role={click ? 'button' : pending ? 'status' : undefined} tabIndex={click ? 0 : undefined}
      aria-label={`${name === 'email' ? uiText("Email") : uiText("Phone")}: ${label}`} aria-busy={pending || undefined}
      onClick={click} onKeyDown={event => { if (click && ['Enter', ' '].includes(event.key)) { event.preventDefault(); click(); } }}
      style={{ cursor: click ? 'pointer' : 'default' }}>
      <i className={`fa-solid ${icon} tp-contact-icon`}></i>
      <span>{uiText(label)}</span>
      {(value || canLookup || pending) && <i aria-hidden="true"
        className={`fa-solid ${pending ? 'fa-spinner fa-spin' : value ? copied ? 'fa-check' : 'fa-copy' : 'fa-unlock'}`}
        style={{ fontSize: 12, marginInlineStart: 4 }}></i>}
    </div>;
  };

  return <>
    {field('email', email, 'fa-envelope', onCopyEmail, emailCopied)}
    {field('phone', phone, 'fa-phone', onCopyPhone, phoneCopied)}
    {error && <span className="tp-contact-item" role="alert">
      {t('talentProfile.contactLookupUnavailable', 'Contact lookup could not complete. Please try again shortly.')}
    </span>}
  </>;
}
